'use client';
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';

interface OnboardingStep {
  id: string;
  title: string;
  desc: string;
  icon: string;
  action?: string;
}

const steps: OnboardingStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to Smartcontrax',
    desc: 'Your AI-powered smart contract engineering platform. Let\'s get you set up in under 2 minutes.',
    icon: 'SparklesIcon',
  },
  {
    id: 'ecosystem',
    title: 'Choose your ecosystem',
    desc: 'What blockchain are you building on? You can always change this later.',
    icon: 'CubeIcon',
    action: 'ecosystem',
  },
  {
    id: 'first-project',
    title: 'Create your first project',
    desc: 'Give your project a name. Each project has its own workspace, version history, and audit reports.',
    icon: 'FolderOpenIcon',
    action: 'project',
  },
  {
    id: 'ready',
    title: 'You\'re all set!',
    desc: 'Your workspace is ready. Start by writing a contract or asking the AI to generate one for you.',
    icon: 'RocketLaunchIcon',
  },
];

const ecosystems = [
  { id: 'solidity', label: 'Solidity / EVM', icon: '⟠', desc: 'Ethereum, Base, Arbitrum, Polygon', network: 'ethereum' },
  { id: 'rust', label: 'Rust / Anchor', icon: '◎', desc: 'Solana, SVM chains', network: 'solana-devnet' },
  { id: 'vyper', label: 'Vyper', icon: '🐍', desc: 'EVM-compatible, Python-like syntax', network: 'ethereum' },
];

// ─── Default files per ecosystem ─────────────────────────────────────────────

function getDefaultFiles(ecosystem: string, projectName: string) {
  if (ecosystem === 'rust') {
    return [
      {
        file_path: 'programs/contract/src/lib.rs',
        content: `use anchor_lang::prelude::*;\n\ndeclare_id!("11111111111111111111111111111111");\n\n#[program]\npub mod contract {\n    use super::*;\n\n    pub fn initialize(ctx: Context<Initialize>) -> Result<()> {\n        Ok(())\n    }\n}\n\n#[derive(Accounts)]\npub struct Initialize {}\n`,
        language: 'rust',
      },
    ];
  }
  const contractName = projectName.replace(/[^a-zA-Z0-9]/g, '') || 'MyContract';
  return [
    {
      file_path: `contracts/${contractName}.sol`,
      content: `// SPDX-License-Identifier: MIT\npragma solidity ^0.8.24;\n\n/// @title ${contractName}\n/// @notice Generated by Smartcontrax\ncontract ${contractName} {\n    address public owner;\n\n    constructor() {\n        owner = msg.sender;\n    }\n}\n`,
      language: 'solidity',
    },
  ];
}

interface OnboardingFlowProps {
  onComplete: (data: { ecosystem: string; projectName: string }) => void;
  onSkip: () => void;
}

export default function OnboardingFlow({ onComplete, onSkip }: OnboardingFlowProps) {
  const { user } = useAuth();
  const router = useRouter();
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedEcosystem, setSelectedEcosystem] = useState('solidity');
  const [projectName, setProjectName] = useState('');
  const [projectError, setProjectError] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  const step = steps[currentStep];
  const isLast = currentStep === steps.length - 1;
  const isFirst = currentStep === 0;

  const handleNext = async () => {
    if (step.action === 'project') {
      if (!projectName.trim()) {
        setProjectError('Please enter a project name.');
        return;
      }
      if (projectName.trim().length < 2) {
        setProjectError('Project name must be at least 2 characters.');
        return;
      }
      setProjectError('');
    }

    if (isLast) {
      await handleFinish();
      return;
    }
    setCurrentStep((s) => s + 1);
  };

  const handleFinish = async () => {
    if (!user) {
      onComplete({ ecosystem: selectedEcosystem, projectName: projectName.trim() || 'My First Project' });
      return;
    }

    setIsCreating(true);
    setCreateError('');

    try {
      const supabase = createClient();
      const eco = ecosystems.find((e) => e.id === selectedEcosystem);
      const name = projectName.trim() || 'My First Project';

      // 1. Create the project
      const { data: project, error: projErr } = await supabase
        .from('projects')
        .insert({
          owner_id: user.id,
          name,
          description: `${eco?.label ?? 'Solidity'} smart contract project`,
          ecosystem: selectedEcosystem as any,
          network: eco?.network ?? 'ethereum',
          project_status: 'active',
          version: 'v1.0',
        })
        .select()
        .single();

      if (projErr) throw projErr;

      // 2. Seed default files
      const defaultFiles = getDefaultFiles(selectedEcosystem, name);
      if (defaultFiles.length > 0) {
        await supabase.from('project_files').insert(
          defaultFiles.map((f) => ({ project_id: project.id, ...f }))
        );
      }

      // 3. Create initial version snapshot
      const snapshot: Record<string, string> = {};
      defaultFiles.forEach((f) => { snapshot[f.file_path] = f.content; });
      await supabase.from('project_versions').insert({
        project_id: project.id,
        version_label: 'v1.0',
        change_summary: 'Initial project creation via onboarding',
        snapshot,
        created_by: user.id,
      });

      // 4. Assign free-tier credits via credit_transactions
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('credits_balance')
        .eq('id', user.id)
        .single();

      const currentBalance = (profile as any)?.credits_balance ?? 0;
      if (currentBalance === 0) {
        // Award 1000 free credits on first project creation
        await supabase.from('credit_transactions').insert({
          user_id: user.id,
          workspace_id: null,
          operation_type: 'topup',
          credits_delta: 1000,
          description: 'Welcome bonus — 1,000 free credits',
          metadata: { source: 'onboarding' },
        });

        await supabase
          .from('user_profiles')
          .update({ credits_balance: 1000 })
          .eq('id', user.id);
      }

      // 5. Persist active project for workspace
      localStorage.setItem('smartcontrax_active_project', JSON.stringify(project));

      onComplete({ ecosystem: selectedEcosystem, projectName: name });
    } catch (e: any) {
      setCreateError(e.message || 'Failed to create project. Please try again.');
      setIsCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Progress */}
        <div className="flex items-center gap-2 mb-8 justify-center">
          {steps.map((s, i) => (
            <div
              key={s.id}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i <= currentStep ? 'bg-primary' : 'bg-border'
              } ${i === currentStep ? 'w-8' : 'w-4'}`}
            />
          ))}
        </div>

        {/* Card */}
        <div className="card-elevated rounded-2xl p-8">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center">
              <Icon name={step.icon as Parameters<typeof Icon>[0]['name']} size={28} className="text-primary" />
            </div>
          </div>

          <h2 className="text-xl font-bold text-foreground text-center mb-2">{step.title}</h2>
          <p className="text-sm text-muted-foreground text-center mb-8 leading-relaxed">{step.desc}</p>

          {/* Ecosystem picker */}
          {step.action === 'ecosystem' && (
            <div className="space-y-3 mb-6">
              {ecosystems.map((eco) => (
                <button
                  key={eco.id}
                  onClick={() => setSelectedEcosystem(eco.id)}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all duration-150 text-left ${
                    selectedEcosystem === eco.id
                      ? 'border-primary bg-primary/10' :'border-border bg-secondary hover:border-primary/40'
                  }`}
                >
                  <span className="text-2xl">{eco.icon}</span>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{eco.label}</p>
                    <p className="text-xs text-muted-foreground">{eco.desc}</p>
                  </div>
                  {selectedEcosystem === eco.id && (
                    <Icon name="CheckCircleIcon" size={18} className="text-primary ml-auto" />
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Project name input */}
          {step.action === 'project' && (
            <div className="mb-6">
              <label className="block text-xs font-medium text-muted-foreground mb-2">
                Project Name
              </label>
              <input
                type="text"
                value={projectName}
                onChange={(e) => {
                  setProjectName(e.target.value);
                  setProjectError('');
                }}
                placeholder="e.g. My DeFi Vault"
                className="input-field"
                maxLength={80}
                autoFocus
              />
              {projectError && (
                <p className="text-xs text-red-400 mt-1.5">{projectError}</p>
              )}
            </div>
          )}

          {/* Ready state */}
          {isLast && (
            <div className="mb-6 space-y-2">
              {[
                'AI workspace ready',
                'Multi-agent consensus enabled',
                'Version control active',
                'Testnet deployment unlocked',
                '1,000 free credits assigned',
              ].map((item) => (
                <div key={item} className="flex items-center gap-2.5 text-sm text-muted-foreground">
                  <Icon name="CheckCircleIcon" size={16} className="text-emerald-400 shrink-0" />
                  {item}
                </div>
              ))}
            </div>
          )}

          {/* Create error */}
          {createError && (
            <div className="mb-4 flex items-start gap-2 p-3 rounded-xl bg-red-400/10 border border-red-400/30">
              <Icon name="ExclamationCircleIcon" size={14} className="text-red-400 shrink-0 mt-0.5" />
              <p className="text-xs text-red-400 leading-relaxed">{createError}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            {!isFirst && !isLast && (
              <button
                onClick={() => setCurrentStep((s) => s - 1)}
                className="btn-secondary flex-1"
                disabled={isCreating}
              >
                Back
              </button>
            )}
            <button
              onClick={handleNext}
              disabled={isCreating}
              className="btn-primary flex-1 justify-center"
            >
              {isCreating ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                  Creating…
                </>
              ) : isLast ? (
                <>
                  Open Workspace
                  <Icon name="ArrowRightIcon" size={14} />
                </>
              ) : (
                <>
                  Continue
                  <Icon name="ArrowRightIcon" size={14} />
                </>
              )}
            </button>
          </div>

          {isFirst && (
            <button
              onClick={onSkip}
              className="w-full mt-3 text-xs text-muted-foreground hover:text-foreground transition-colors text-center"
            >
              Skip onboarding
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
